import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PRAYER_LABELS } from "@/lib/format";
import { MONTHS } from "@/lib/donation";
import { Header } from "@/components/landing/Header";
import { PrayerSection } from "@/components/landing/PrayerSection";
import { ContactSection } from "@/components/landing/ContactSection";

const COLS = ["imsak", "subuh", "terbit", "dhuha", "dzuhur", "ashar", "maghrib", "isya"];

export default function JadwalSholatPublik() {
  const now = new Date();
  const [profile, setProfile] = useState(null);
  const [prayer, setPrayer] = useState(null);
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [monthly, setMonthly] = useState(null);

  useEffect(() => {
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/prayer/today").then((r) => setPrayer(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setMonthly(null);
    api.get(`/prayer/month?year=${ym.year}&month=${ym.month}`).then((r) => setMonthly(r.data)).catch(() => setMonthly({ jadwal: [] }));
  }, [ym]);

  const shift = (d) => {
    let m = ym.month + d, y = ym.year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYm({ year: y, month: m });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="prayer-page">
      <Header profile={profile} />
      <section className="emerald-bg pattern-overlay text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-emerald-200 hover:text-white" data-testid="prayer-back-home"><ArrowLeft size={16} /> Kembali ke beranda</Link>
          <p className="mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-[#80b996]">Jadwal Sholat Lengkap</p>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Waktu Sholat Kab. Ciamis</h1>
          <p className="mt-3 text-emerald-100/85 max-w-2xl">Jadwal harian dan bulanan untuk wilayah Kabupaten Ciamis, Jawa Barat. {prayer?.source === "auto" ? "Bersumber dari data Kemenag RI" : "Ditetapkan oleh pengurus DKM"}, waktu dalam WIB.</p>
        </div>
      </section>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">
        <PrayerSection prayer={prayer} />
        <section data-testid="prayer-monthly">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-700">Jadwal Bulanan</p>
              <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-emerald-950" data-testid="prayer-month-title">{MONTHS[ym.month - 1]} {ym.year}</h2>
              <p className="mt-2 text-sm text-slate-600">Tabel waktu sholat setiap hari, hari ini ditandai dengan warna emas.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => shift(-1)} className="h-10 w-10 rounded-full border border-emerald-900/15 flex items-center justify-center hover:bg-emerald-50" data-testid="prayer-prev-month" aria-label="Bulan sebelumnya"><ChevronLeft size={18} /></button>
              <button onClick={() => shift(1)} className="h-10 w-10 rounded-full border border-emerald-900/15 flex items-center justify-center hover:bg-emerald-50" data-testid="prayer-next-month" aria-label="Bulan berikutnya"><ChevronRight size={18} /></button>
            </div>
          </div>
          <div className="mt-8 rounded-3xl bg-white border border-emerald-900/10 overflow-x-auto">
            <table className="w-full text-sm" data-testid="prayer-month-table">
              <thead className="emerald-bg text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Tanggal</th>
                  {COLS.map((c) => <th key={c} className="px-3 py-3 font-semibold">{PRAYER_LABELS[c]}</th>)}
                </tr>
              </thead>
              <tbody>
                {!monthly && <tr><td colSpan={9} className="text-center py-10 text-slate-400"><Loader2 className="animate-spin inline mr-2" size={16} />Memuat jadwal...</td></tr>}
                {monthly?.jadwal?.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-400">Jadwal tidak tersedia.</td></tr>}
                {monthly?.jadwal?.map((d) => {
                  const isToday = d.date === prayer?.date;
                  return (
                    <tr key={d.date} className={`border-t border-emerald-900/5 ${isToday ? "bg-[#f6ebd0] font-semibold" : "odd:bg-[#fcfdf9]"}`} data-testid={isToday ? "prayer-month-today" : undefined}>
                      <td className="px-4 py-2.5 text-left whitespace-nowrap">{d.tanggal}</td>
                      {COLS.map((c) => <td key={c} className="px-3 py-2.5 text-center tabular-nums">{d[c]}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>
      <ContactSection profile={profile} />
    </div>
  );
}
